/**
 * LevelRegistry — single source of truth for every level in the game.
 *
 * Replaces three scattered definitions:
 *   - `LEVEL_DISPLAY_NAMES` in main.ts
 *   - `LEVELS` / `LevelInfo` in LevelSelect.ts
 *   - The hard-coded switch/case in main.ts' `loadLevel()`
 *
 * To add a new level:
 *   1. Implement `setupMyLevel(scene)` in src-babylon/scenes/MyLevel.ts
 *   2. Add a SCENE_KEYS entry in GameConfig.ts
 *   3. Append a LevelDefinition to ALL_LEVELS below
 *   4. (optional) Add a MechanicContract in contracts/ for design-rule testing
 *
 * The registry is the only place that needs to change for the rest of the
 * game (LevelSelect, main.ts, save system) to pick up the new level.
 */
import { Vector3 } from "@babylonjs/core/Maths/math.vector";
import { Color3, Color4 } from "@babylonjs/core/Maths/math.color";
import { SCENE_KEYS, OVERGROWN_CONSERVATORY, SCRAPYARD, CLOCKWORK_CANOPY } from "../config/GameConfig";
import { setupLiminalAtrium } from "../scenes/LiminalAtrium";
import { setupRedwoodScrapyard } from "../scenes/RedwoodScrapyard";
import { setupOvergrownConservatory } from "../scenes/OvergrownConservatory";
import { setupClockworkCanopy } from "../scenes/ClockworkCanopy";
import type { LevelDefinition } from "./LevelDefinition";

// ── Shared asset paths ───────────────────────────────────────────────
//
// Both cat models are required by every level (PlayerEntity loads them
// through AssetManager during BaseLevel setup), so they live as
// constants used by every manifest below. Interactive paths are inlined
// per-level so each entry is self-documenting about which interactives
// the level actually constructs.
const CHARACTER_MODELS: readonly string[] = [
  "/assets/models/minna.glb",
  "/assets/models/basil.glb",
];

// ── Level Definitions ────────────────────────────────────────────────
//
// Spawn positions, clear colors, and fog values are copied from the
// `setupBaseLevel(scene, { ... })` call in each scene file. When editing
// these, update both the registry entry here AND the scene file's expectations.

const LIMINAL_ATRIUM: LevelDefinition = {
  key: SCENE_KEYS.LIMINAL_ATRIUM,
  displayName: "The Liminal Atrium",
  subtitle: "Tutorial — Dusty Victorian Sunroom",
  number: 0,
  prerequisite: null,
  reward: null,

  clearColor: new Color4(0.03, 0.015, 0.06, 1.0),
  fogColor: new Color3(0.06, 0.03, 0.1),
  fogDensity: 0.018,
  // NOTE: LiminalAtrium.ts passes its own spawn + levelFit to setupBaseLevel
  // directly and ignores these registry values. These entries are kept in
  // sync only for LevelSelect preview / design docs; the real spawn lives
  // in `src-babylon/scenes/LiminalAtrium.ts`.
  minnaSpawn: new Vector3(-1.5, 3.2, 0),
  basilSpawn: new Vector3(-0.4, 3.2, 0),

  // TutorialGate puzzle uses a pressure plate, dial, crystal receiver, and
  // portal; the scene shell loads atrium_env.glb as the visual environment.
  assetManifest: {
    environment: "/assets/models/environment/atrium_env.glb",
    characters: [...CHARACTER_MODELS],
    interactives: [
      "/assets/models/environment/pressure_plate.glb",
      "/assets/models/environment/dial.glb",
      "/assets/models/environment/crystal_receiver.glb",
      "/assets/models/environment/portal_frame.glb",
    ],
  },

  // Mid art-pass: env GLB + interactive GLBs are final, but the readability
  // cue rig (cue lights, conduits, scaffolds) is still procedural.
  artStatus: "in-progress",

  // Tutorial HUD shows only the six base abilities the level actually teaches.
  // Co-op abilities (Resonant Drop, Chronos Bubble) are unlocked in later levels.
  hudAbilityIds: [
    "auraSight",
    "shadowSlip",
    "calmingResonance",
    "turnTableTouch",
    "pneumaticAnchor",
    "overclockedLeap",
  ],

  setup: setupLiminalAtrium,
};

const REDWOOD_SCRAPYARD: LevelDefinition = {
  key: SCENE_KEYS.REDWOOD_SCRAPYARD,
  displayName: "The Redwood Scrapyard",
  subtitle: "Giant redwoods & rusted machinery",
  number: 1,
  prerequisite: SCENE_KEYS.LIMINAL_ATRIUM,
  reward: "resonantDrop",

  clearColor: new Color4(0.04, 0.03, 0.09, 1.0),
  fogColor: Color3.FromHexString(SCRAPYARD.COLORS.REDWOOD_DARK),
  fogDensity: 0.012,
  minnaSpawn: new Vector3(6, 4, 0),
  basilSpawn: new Vector3(8, 4, 0),

  // SeizedCrane uses a pressure plate (CraneLever is procedural).
  // CrowSquirrel uses food items (NestPlatform is procedural).
  // KittenCorral, HoodieJigsaw use procedural geometry.
  // FinalBlockage uses a rusted blockage.
  // The scene shell loads scrapyard_env.glb and an exit Portal.
  assetManifest: {
    environment: "/assets/models/environment/scrapyard_env.glb",
    characters: [...CHARACTER_MODELS],
    interactives: [
      "/assets/models/environment/pressure_plate.glb",
      "/assets/models/environment/food_item.glb",
      "/assets/models/environment/rusted_blockage.glb",
      "/assets/models/environment/portal_frame.glb",
    ],
  },

  // Mid art-pass: env GLB loaded, but background trees/fog/decals are procedural.
  artStatus: "in-progress",

  // L1 teaches the 6 base abilities. Resonant Drop is the REWARD
  // from Elder Kitty at the end of L1 — unlocked for use in L2, not
  // L1 itself. Prior list included "resonantDrop" as available from
  // spawn, which put an unlocked "Drop" icon in the ability bar with
  // no keybind that worked — misleading to the player.
  hudAbilityIds: [
    "auraSight",
    "shadowSlip",
    "calmingResonance",
    "turnTableTouch",
    "pneumaticAnchor",
    "overclockedLeap",
  ],

  setup: setupRedwoodScrapyard,
};

const OVERGROWN_CONSERVATORY_LEVEL: LevelDefinition = {
  key: SCENE_KEYS.OVERGROWN_CONSERVATORY,
  displayName: "The Overgrown Conservatory",
  subtitle: "Glasshouse mist, valves, and feral flora",
  number: 2,
  prerequisite: SCENE_KEYS.REDWOOD_SCRAPYARD,
  reward: "chronosBubble",

  clearColor: Color4.FromHexString(OVERGROWN_CONSERVATORY.COLORS.BURROW_DEEP + "ff"),
  fogColor: Color3.FromHexString(OVERGROWN_CONSERVATORY.COLORS.BURROW_EARTH),
  fogDensity: 0.025,
  minnaSpawn: new Vector3(5, 3, 0),
  basilSpawn: new Vector3(10, 3, 0),

  // MasterValve uses ValveCrank entities (procedural brass pedestals +
  // torus wheels). NectarEscort owns the fox/opossum/nest NPCs. The
  // Boiler-Bound Sundew apex loads `conservatory_boiler_sundew.glb` as
  // its hero prop. Scene shell now loads the Victorian glass dome
  // environment via `overgrown_conservatory_env.glb` (Blender-authored
  // iron ribs, glass panels, iron-wood vines, bioluminescent ferns,
  // petrified layer cakes, copper pipes).
  assetManifest: {
    environment: "/assets/models/environment/overgrown_conservatory_env.glb",
    characters: [...CHARACTER_MODELS],
    interactives: [
      "/assets/models/environment/conservatory_mist_regulator.glb",
      "/assets/models/environment/conservatory_boiler_sundew.glb",
    ],
  },

  // Mid art-pass: env GLB loaded, but the valve/sundew interactives are
  // still procedural placeholders layered on top of the hero GLBs.
  artStatus: "in-progress",

  // L2 player arrives with Resonant Drop (earned from Elder Kitty at
  // end of L1). Chronos Bubble is the L2 reward — unlocked for use in
  // L3, not L2 itself. Prior list showed Bubble as unlocked from
  // spawn despite no keybind resolving.
  hudAbilityIds: [
    "auraSight",
    "shadowSlip",
    "calmingResonance",
    "turnTableTouch",
    "pneumaticAnchor",
    "overclockedLeap",
    "resonantDrop",
  ],

  setup: setupOvergrownConservatory,
};

const CLOCKWORK_CANOPY_LEVEL: LevelDefinition = {
  key: SCENE_KEYS.CLOCKWORK_CANOPY,
  displayName: "The Clockwork Canopy",
  subtitle: "Redwood peaks & clockwork observatories",
  number: 3,
  prerequisite: SCENE_KEYS.OVERGROWN_CONSERVATORY,
  reward: null,

  clearColor: new Color4(0.02, 0.04, 0.1, 1.0),
  fogColor: Color3.FromHexString(CLOCKWORK_CANOPY.COLORS.GOLD_FOG),
  fogDensity: 0.008,
  minnaSpawn: new Vector3(6, 4, 0),
  basilSpawn: new Vector3(8, 4, 0),

  // GearAlignment uses a dial. LightPath uses a crystal receiver.
  // ShadowRelay uses a pressure plate + crystal receiver.
  // TheOrrery uses a pressure plate + portal + brass_orrery.glb hero +
  // three canonical lens-ring dials. Scene shell loads the authored
  // Canopy environment (redwood peak silhouettes, wooden observatory
  // decks with brass rims and tilted lens discs, the apex ornamental
  // gear, starfield background) from clockwork_canopy_env.glb.
  assetManifest: {
    environment: "/assets/models/environment/clockwork_canopy_env.glb",
    characters: [...CHARACTER_MODELS],
    interactives: [
      "/assets/models/environment/dial.glb",
      "/assets/models/environment/crystal_receiver.glb",
      "/assets/models/environment/pressure_plate.glb",
      "/assets/models/environment/portal_frame.glb",
    ],
  },

  // Mid art-pass: heavy procedural content (gears, orrery, constellation
  // segments) but interactive props use shared GLB pipeline.
  artStatus: "in-progress",

  hudAbilityIds: [
    "auraSight",
    "shadowSlip",
    "calmingResonance",
    "turnTableTouch",
    "pneumaticAnchor",
    "overclockedLeap",
    "resonantDrop",
    "chronosBubble",
  ],

  setup: setupClockworkCanopy,
};

/**
 * All levels in progression order.
 *
 * The order here defines the LevelSelect card order AND the `next level`
 * advancement logic after LEVEL_COMPLETE.
 */
export const ALL_LEVELS: readonly LevelDefinition[] = [
  LIMINAL_ATRIUM,
  REDWOOD_SCRAPYARD,
  OVERGROWN_CONSERVATORY_LEVEL,
  CLOCKWORK_CANOPY_LEVEL,
] as const;

// ── Helpers ──────────────────────────────────────────────────────────

/** Find a level by its scene key. Returns undefined if the key is unknown. */
export function getLevelByKey(key: string): LevelDefinition | undefined {
  return ALL_LEVELS.find(level => level.key === key);
}

/**
 * Get the next level in the progression order, or `undefined` if this is the
 * final level (or if `key` is unknown).
 */
export function getNextLevel(key: string): LevelDefinition | undefined {
  const index = ALL_LEVELS.findIndex(level => level.key === key);
  if (index < 0 || index >= ALL_LEVELS.length - 1) {
    return undefined;
  }
  return ALL_LEVELS[index + 1];
}

/**
 * Walk the prerequisite chain and return every level key that must be complete
 * before `key` unlocks. The returned array is ordered from oldest prerequisite
 * to most-recent (i.e. `[level0, level1, ...]`).
 *
 * Returns an empty array if `key` has no prerequisites. Returns an empty array
 * if the key is unknown.
 */
export function getLevelPrerequisites(key: string): string[] {
  const level = getLevelByKey(key);
  if (!level) return [];

  const chain: string[] = [];
  let current: LevelDefinition | undefined = level;
  // Guard against malformed/circular prerequisite chains.
  const seen = new Set<string>();
  while (current?.prerequisite) {
    if (seen.has(current.prerequisite)) break;
    seen.add(current.prerequisite);
    const parent = getLevelByKey(current.prerequisite);
    if (!parent) break;
    chain.unshift(parent.key);
    current = parent;
  }
  return chain;
}
